import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tooltip,
  Divider,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { FC, useState, useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import { EPrePartnerType, getPartnerKey, IPrePartner } from 'common/create-banking-account.dto';
import { maskCpf } from 'utils/banking';
import { cpf } from 'cpf-cnpj-validator';
import AddPartnerModal from '../modals/AddPartnerModal';
import DeletePartnerModal from '../modals/DeletePartnerModal';

const PartnerTable = ({
  partners,
  isAnotherRepresentative,

  onSetResponsible,
  onDelete,
}: {
  partners: IPrePartner[];
  isAnotherRepresentative: boolean;
  onSetResponsible: (document: string) => void;
  onDelete: (document: string) => void;
}) => {
  const [isResponsibleDisabled, setIsResponsibleDisabled] = useState(false);

  useEffect(() => {
    setIsResponsibleDisabled(isAnotherRepresentative);
  }, [isAnotherRepresentative]);

  return (
    <Box mb={2}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
              <TableCell align="center">
                <Typography fontWeight={400} fontSize={14}>
                  Tipo
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={400} fontSize={14}>
                  Documento
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={400} fontSize={14}>
                  Nome ou Razão Social
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={400} fontSize={14}>
                  Participação
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={400} fontSize={14}>
                  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
                    Responsável pela abertura
                    <Tooltip
                      sx={{ textAlign: 'center' }}
                      title="O responsável é a pessoa que irá receber o e-mail para validação da biometria e finalizar a abertura da conta."
                    >
                      <IconButton size="small" sx={{ p: 0.5 }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <br />
                  (Biometria e envio de documentação)
                </Typography>
              </TableCell>

              <TableCell align="center" />
            </TableRow>
          </TableHead>
          <TableBody>
            {partners.map((partner) => (
              <TableRow key={getPartnerKey(partner)}>
                <TableCell align="center">
                  <Typography fontWeight={400} fontSize={14}>
                    {partner.type === 'PHYSICAL' ? 'Pessoa física' : 'Pessoa jurídica'}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={400} fontSize={14}>
                    {partner.type === 'PHYSICAL' ? partner.document : partner.cnpj}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={400} fontSize={14}>
                    {partner.type === 'PHYSICAL' ? partner.name : partner.companyName}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography fontWeight={400} fontSize={14}>
                    {partner.participation}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Radio
                    checked={partner.responsible}
                    onChange={() => onSetResponsible(getPartnerKey(partner))}
                    disabled={isResponsibleDisabled}
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    onClick={() => {
                      onDelete(getPartnerKey(partner));
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

interface RepresentativeFormProps {
  partners: IPrePartner[];
  isAnotherRepresentative: string;
  representativePartner?: IPrePartner;
  setPartners: React.Dispatch<React.SetStateAction<IPrePartner[]>>;
  setIsAnotherRepresentative: React.Dispatch<React.SetStateAction<string>>;
}

const RepresentativeForm = ({
  partners,
  isAnotherRepresentative,
  representativePartner,
  setPartners,
  setIsAnotherRepresentative,
}: RepresentativeFormProps) => {
  const [form, setForm] = useState({
    name: representativePartner ? representativePartner.name : '',
    document: representativePartner ? representativePartner.document : '',
  });

  const [repDocumentError, setRepDocumentError] = useState<string | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);

  const removeRepresentative = () => {
    setPartners((prev) => prev.filter((p) => !(p.legalRepresentative && p.fromRepresentativeForm)));
    setForm({ name: '', document: '' });
  };

  const handleIsAnotherRepresentativeChange = (value: string) => {
    setIsAnotherRepresentative(value);

    if (value === 'yes') {
      setPartners((prev) => prev.map((p) => ({ ...p, responsible: false })));
    }
  };

  const validateAndSubmitForm = () => {
    let hasNameError = false;
    let hasDocumentError = false;

    if (!form.name || form.name.trim().split(/\s+/).length <= 1) {
      setNameError('Informe o nome completo do representante, incluindo sobrenome.');
      hasNameError = true;
    }

    if (!form.document) {
      setRepDocumentError('CPF do representante é obrigatório.');
      hasDocumentError = true;
    }

    if (form.document && !cpf.isValid(form.document.replace(/\D/g, ''))) {
      setRepDocumentError('CPF Inválido.');
      hasDocumentError = true;
    }

    if (
      form.document &&
      partners.some((p) => p.type === EPrePartnerType.PHYSICAL && p.document === form.document && !p.fromRepresentativeForm)
    ) {
      setRepDocumentError('Este CPF já foi adicionado como sócio.');
      hasDocumentError = true;
    }

    if (!hasNameError) {
      setNameError(null);
    }

    if (!hasDocumentError) {
      setRepDocumentError(null);
    }

    setPartners((prev) => {
      const filtered = prev.filter((p) => !p.fromRepresentativeForm);

      return [
        ...filtered,
        {
          type: EPrePartnerType.PHYSICAL,
          cnpj: '',
          document: form.document,
          name: form.name,
          participation: '0%', // ? representante legal não tem participação definida, pode ser 0%
          responsible: true,
          legalRepresentative: true,
          fromRepresentativeForm: true,
          isError: hasNameError || hasDocumentError,
        },
      ];
    });
  };

  useEffect(() => {
    if (representativePartner) {
      setIsAnotherRepresentative('yes');
      setForm({ name: representativePartner.name, document: representativePartner.document });
    } else {
      setIsAnotherRepresentative('no');
      setForm({ name: '', document: '' });
    }
  }, [representativePartner]);

  useEffect(() => {
    if (isAnotherRepresentative === 'no') {
      removeRepresentative();
    }
  }, [isAnotherRepresentative]);

  return (
    <Box>
      <Typography fontWeight={600} fontSize={16} mb={2}>
        A abertura da conta será realizada por um representante legal diferente dos sócios informados acima?
      </Typography>

      <RadioGroup row value={isAnotherRepresentative} onChange={(e) => handleIsAnotherRepresentativeChange(e.target.value)}>
        <FormControlLabel value="yes" control={<Radio />} label="Sim" />
        <FormControlLabel value="no" control={<Radio />} label="Não" />
      </RadioGroup>

      {isAnotherRepresentative === 'yes' && (
        <Box display="flex" flexDirection="column" gap={2} mt={2}>
          <Typography fontWeight={600} fontSize={16}>
            Informe os dados do representante legal que irá realizar a abertura da conta.
          </Typography>

          <TextField
            label="Nome completo"
            variant="standard"
            fullWidth
            margin="dense"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onBlur={() => {
              validateAndSubmitForm();
            }}
            error={!!nameError}
            helperText={nameError}
          />

          <TextField
            label="Número do CPF"
            variant="standard"
            fullWidth
            margin="dense"
            value={form.document}
            onChange={(e) => {
              let input = e.target.value;
              if (input.length > 14) input = input.slice(0, 14);
              setForm({ ...form, document: maskCpf(input) });
            }}
            onBlur={() => {
              validateAndSubmitForm();
            }}
            placeholder="000.000.000-00"
            inputProps={{ maxLength: 14 }}
            error={!!repDocumentError}
            helperText={repDocumentError}
          />
        </Box>
      )}
    </Box>
  );
};

interface PartnerPreRegisterProps {
  partners: IPrePartner[];
  setPartners: React.Dispatch<React.SetStateAction<IPrePartner[]>>;
}

const PartnerPreRegister: FC<PartnerPreRegisterProps> = ({ partners = [], setPartners }) => {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [partnerToDelete, setPartnerToDelete] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();

  const [isAnotherRepresentative, setIsAnotherRepresentative] = useState(
    partners.some((p) => p.legalRepresentative && p.fromRepresentativeForm) ? 'yes' : 'no'
  );

  const onAdd = (partner: IPrePartner) => {
    const isDocumentExist = partners.some((p) => getPartnerKey(p) === getPartnerKey(partner));

    if (isDocumentExist) {
      const message = 'Sócio com esse documento já foi adicionado!';
      enqueueSnackbar(message, { variant: 'error' });
      throw new Error(message);
    }

    setPartners((prev) => {
      return [...prev, partner];
    });

    setAddModalOpen(false);
  };

  const onDelete = () => {
    setPartners((prev) => prev.filter((p) => getPartnerKey(p) !== partnerToDelete));
    setDeleteModalOpen(false);
  };

  const onDeleteModalOpen = (document: string) => {
    setPartnerToDelete(document);
    setDeleteModalOpen(true);
  };

  const onSetResponsible = (key: string) => {
    setPartners((prev) =>
      prev.map((p) => ({
        ...p,
        responsible: getPartnerKey(p) === key,
      }))
    );
  };

  const representativePartner = partners.find((p) => p.legalRepresentative && p.fromRepresentativeForm);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography fontWeight={600} fontSize={22}>
            Sócios da empresa
          </Typography>

          <Typography fontWeight={400} fontSize={16}>
            Informe os sócios da empresa.
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="primary"
          sx={{ height: 48, textTransform: 'none', fontWeight: 600, fontSize: 16 }}
          onClick={() => setAddModalOpen(true)}
        >
          Adicionar sócio
        </Button>
      </Box>

      {partners.filter((p) => !p.fromRepresentativeForm).length > 0 && (
        <PartnerTable
          partners={partners.filter((p) => !p.fromRepresentativeForm)}
          isAnotherRepresentative={isAnotherRepresentative === 'yes'}
          onSetResponsible={onSetResponsible}
          onDelete={onDeleteModalOpen}
        />
      )}

      <Divider sx={{ mb: 2 }} />

      <RepresentativeForm
        partners={partners}
        setPartners={setPartners}
        isAnotherRepresentative={isAnotherRepresentative}
        representativePartner={representativePartner}
        setIsAnotherRepresentative={setIsAnotherRepresentative}
      />

      <AddPartnerModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onAdd={onAdd} />

      <DeletePartnerModal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} onConfirm={onDelete} />
    </>
  );
};

export default PartnerPreRegister;
